import React, { useState } from 'react';
import { Text } from '@gbm-tech/typography';
import * as SelectPrimitive from '@radix-ui/react-select';
import { CaretDown, Check } from 'phosphor-react';

import { SelectContainer, SelectGroupContainer, SelectStyled } from './styles';

export type SelectItem = {
  value: string;
  label: string;
};

export type SelectItemGroup = {
  label?: string;
  items: SelectItem[];
};

export type SelectProps = React.ComponentPropsWithRef<'div'> & {
  size?: 'sm' | 'md' | 'lg';

  width?: string;

  full?: boolean;
  withoutBorder?: boolean;

  itemGroups: SelectItemGroup[];

  iconProps?: SelectPrimitive.SelectIconProps;
  selectProps?: SelectPrimitive.SelectProps;
  valueProps?: SelectPrimitive.SelectValueProps;
};

export const Select: React.FC<SelectProps> = ({
  size,

  width = '',

  full = true,
  withoutBorder = false,

  itemGroups = [{ label: '', items: [{ label: '', value: '' }] }],

  iconProps,
  selectProps,
  valueProps,

  placeholder = '',
  ...props
}) => {
  const [selectedValue, setSelectedValue] = useState<string>('');

  return (
    <SelectContainer
      size={size}
      withoutBorder={withoutBorder}
      full={full}
      css={{ $$width: width }}
      {...props}
    >
      <SelectPrimitive.Root
        value={!!selectedValue ? selectedValue : undefined}
        onValueChange={setSelectedValue}
        {...selectProps}
      >
        <SelectStyled.Trigger title={placeholder} size={size}>
          <SelectStyled.Value
            className="span-select-value"
            placeholder={placeholder}
            {...valueProps}
          />

          <SelectStyled.Icon {...iconProps}>
            <CaretDown />
          </SelectStyled.Icon>
        </SelectStyled.Trigger>

        <SelectContent>
          <SelectStyled.ScrollUpButton>
            <CaretDown />
          </SelectStyled.ScrollUpButton>

          <SelectStyled.ViewPort>
            {itemGroups.length > 0 &&
              itemGroups.map((itemGroup, index) => (
                <SelectStyled.Group
                  key={`select-item-group-${itemGroup.label}-${index}`}
                >
                  <SelectStyled.Label size={size}>
                    {itemGroup?.label ? itemGroup.label : ''}
                  </SelectStyled.Label>

                  {itemGroup?.items &&
                    itemGroup.items.map((item, index) => (
                      <SelectStyled.Item
                        value={item.value}
                        size={size}
                        key={`select-item-${item.value}-${index}`}
                      >
                        <SelectStyled.ItemText>
                          {item.label}
                        </SelectStyled.ItemText>

                        <SelectStyled.ItemIndicator>
                          <Check weight="bold" />
                        </SelectStyled.ItemIndicator>
                      </SelectStyled.Item>
                    ))}
                </SelectStyled.Group>
              ))}
          </SelectStyled.ViewPort>
        </SelectContent>
      </SelectPrimitive.Root>
    </SelectContainer>
  );
};

export type SelectContentProps = SelectPrimitive.SelectContentProps & {
  children: React.ReactNode;
};

export const SelectContent: React.FC<SelectContentProps> = ({
  children,
  ...props
}) => {
  return (
    <SelectPrimitive.Portal>
      <SelectStyled.Content {...props}>{children}</SelectStyled.Content>
    </SelectPrimitive.Portal>
  );
};

export type SelectGroupProps = React.ComponentPropsWithRef<'div'> & {
  size?: 'sm' | 'md' | 'lg';

  width?: string;
  label?: string;

  full?: boolean;

  selectProps?: SelectProps;
};

export const SelectGroup: React.FC<SelectGroupProps> = ({
  size,

  width = '',
  label,

  full = false,

  selectProps,
  placeholder = '',

  ...props
}) => {
  return (
    <SelectGroupContainer full={full} css={{ $$width: width }} {...props}>
      <Text size="sm">{label && label}</Text>

      <Select
        full
        placeholder={placeholder}
        itemGroups={selectProps?.itemGroups ? selectProps?.itemGroups : []}
        {...selectProps}
      />
    </SelectGroupContainer>
  );
};
